import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getPhotoStudioAppointments, updatePhotoStudioStatus, savePhotoStudioAppointment } from '../services/photoStudioService';
import { sendStatusChangeEmail } from '../services/emailService';
import { addHoliday, removeHoliday, getHolidays } from '../services/holidayService';
import adminAuthService from '../services/adminAuthService';
import queueService from '../services/dynamoQueueService';
import { generateSequentialToken, calculateEstimatedTime } from '../utils/tokenGenerator';
import CalendarView from './CalendarView';
import RealTimeQueue from './RealTimeQueue';
import socketService from '../services/socketService';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ 
    startDate: new Date().toISOString().split('T')[0], 
    endDate: new Date().toISOString().split('T')[0] 
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudio, setSelectedStudio] = useState('All Studios');
  const [holidays, setHolidays] = useState([]);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayReason, setNewHolidayReason] = useState('');
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, calendar, queue
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [walkInEmail, setWalkInEmail] = useState('');
  const [walkInStudio, setWalkInStudio] = useState('BLR13');
  const [walkInLoading, setWalkInLoading] = useState(false);
  const itemsPerPage = 50;

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const result = await getPhotoStudioAppointments();
      
      if (result.success) {
        setSubmissions(result.data);
        setError(null);
      } else {
        setError(result.message || 'Failed to load appointments');
      }
    } catch (err) {
      setError('Failed to load submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const adminUser = adminAuthService.getCurrentUser();
    
    if (!adminUser) {
      setError('Admin authentication required');
      return;
    }

    // Admin is authenticated via localStorage (ProtectedAdminRoute handles login)
    // No backend JWT verification needed — the route guard already ensures auth
    
    console.log('=== STATUS UPDATE DEBUG ===');
    console.log('ID:', id);
    console.log('New Status:', newStatus);
    console.log('Admin Email:', adminUser);
    
    try {
      const currentAppointment = submissions.find(sub => sub.id === id);
      const oldStatus = currentAppointment?.status;
      
      console.log('Current Appointment:', currentAppointment);
      console.log('Old Status:', oldStatus);
      
      const result = await updatePhotoStudioStatus(id, null, adminUser, newStatus);
      console.log('Update Result:', result);
      
      if (result.success) {
        const completionTime = newStatus === 'Completed' ? new Date().toLocaleString() : null;
        setSubmissions(prev => prev.map(sub => 
          sub.id === id ? { ...sub, status: newStatus, updatedBy: adminUser, completionTime } : sub
        ));
        
        if (oldStatus !== newStatus && currentAppointment) {
          try {
            const emailResult = await sendStatusChangeEmail(
              currentAppointment.email,
              adminUser,
              currentAppointment,
              newStatus,
              oldStatus
            );
            
            if (emailResult.success) {
              console.log('Status change email sent successfully');
            } else {
              console.warn('Status change email failed:', emailResult.message);
            }
          } catch (emailError) {
            console.error('Error sending status change email:', emailError);
          }
        }
        
        // Status updated silently - no popup needed
      } else {
        console.error('Update failed:', result.message);
        alert(`Failed to update status: ${result.message}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      console.error('Error details:', error.message, error.stack);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const exportToExcel = () => {
    const filteredData = allFilteredSubmissions;
    
    // Create CSV content
    const headers = ['Token Number', 'Amazon Email ID', 'Studio Location', 'Photo Status', 'Time Slot', 'Queue Position', 'Estimated Time', 'Submitted At', 'Updated By'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map(row => [
        row.tokenNumber || 'N/A',
        row.email || 'N/A',
        `${row.siteCode} Studio`,
        row.status || 'Pending',
        row.estimatedTime ? (row.estimatedTime.includes('|') ? row.estimatedTime.split('|')[1] : row.estimatedTime) : 'N/A',
        `#${row.queuePosition || 'N/A'}`,
        row.estimatedTime ? (row.estimatedTime.includes('|') ? row.estimatedTime.split('|')[0] : row.estimatedTime) : 'N/A',
        row.submittedAt ? new Date(row.submittedAt).toLocaleString() : 'N/A',
        row.updatedBy || 'N/A'
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `photo_studio_appointments_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchHolidays = async () => {
    const result = await getHolidays();
    if (result.success) {
      setHolidays(result.data);
    }
  };

  const handleAddHoliday = async () => {
    if (!newHolidayDate) {
      alert('Please select a date');
      return;
    }
    
    const adminEmail = localStorage.getItem('adminUser');
    const result = await addHoliday(newHolidayDate, newHolidayReason, adminEmail);
    if (result.success) {
      alert('Holiday added successfully');
      setNewHolidayDate('');
      setNewHolidayReason('');
      setShowHolidayForm(false);
      fetchHolidays();
    } else {
      alert('Failed to add holiday');
    }
  };

  const handleRemoveHoliday = async (date) => {
    if (window.confirm('Remove this holiday?')) {
      const result = await removeHoliday(date);
      if (result.success) {
        alert('Holiday removed successfully');
        fetchHolidays();
      } else {
        alert('Failed to remove holiday');
      }
    }
  };

  // Walk-in: auto-assign to current active slot
  const handleWalkIn = async () => {
    if (!walkInEmail || !walkInEmail.endsWith('@amazon.com')) {
      alert('Please enter a valid @amazon.com email');
      return;
    }

    setWalkInLoading(true);
    try {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const today = now.toISOString().split('T')[0];

      // Find the current active slot
      const TIME_SLOTS = [
        { id: '10:00-11:00', start: 600, end: 660 },
        { id: '11:00-12:00', start: 660, end: 720 },
        { id: '12:00-13:00', start: 720, end: 780 },
        { id: '13:00-14:00', start: 780, end: 840 },
        { id: '15:00-16:00', start: 900, end: 960 },
        { id: '16:00-17:00', start: 960, end: 1020 }
      ];

      const activeSlot = TIME_SLOTS.find(s => currentMinutes >= s.start && currentMinutes < s.end);
      if (!activeSlot) {
        alert('No active time slot right now. Studio hours are 10 AM - 2 PM and 3 PM - 5 PM.');
        setWalkInLoading(false);
        return;
      }

      // Get atomic position from Redis
      const posResult = await queueService.getAtomicPosition(walkInStudio, today, activeSlot.id);
      if (posResult.position > 20) {
        alert(`Current slot (${activeSlot.id}) is full. Please try the next slot.`);
        setWalkInLoading(false);
        return;
      }

      const position = posResult.position;
      const token = generateSequentialToken(walkInStudio, position);
      const slotStart = activeSlot.id.split('-')[0];
      const arrivalTime = calculateEstimatedTime(position, slotStart);
      const uniqueId = `${walkInStudio}-${today}-${activeSlot.id}-${position}-${Date.now()}`;

      const formData = {
        id: uniqueId,
        email: walkInEmail.toLowerCase().trim(),
        siteCode: walkInStudio,
        formType: 'photoStudio',
        tokenNumber: token,
        queuePosition: position,
        estimatedTime: `${arrivalTime}|${activeSlot.id}`,
        timeSlot: activeSlot.id,
        submissionDate: new Date().toLocaleString(),
        status: 'Pending',
        photoTaken: 'No'
      };

      const result = await savePhotoStudioAppointment(formData);
      if (result.success) {
        alert(`Walk-in added!\nToken: ${token}\nPosition: #${position}\nEstimated Time: ${arrivalTime}`);
        setWalkInEmail('');
        setShowWalkIn(false);
        fetchSubmissions();
      } else {
        // Rollback Redis position
        await queueService.rollbackPosition(walkInStudio, today, activeSlot.id).catch(() => {});
        alert(`Failed: ${result.message}`);
      }
    } catch (error) {
      alert(`Walk-in failed: ${error.message}`);
    } finally {
      setWalkInLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchHolidays();
    
    // Connect to socket for real-time updates
    socketService.connect();
    socketService.onBookingUpdate(() => {
      fetchSubmissions(); // Refresh data on updates
    });
    
    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchSubmissions();
    }, 30000);
    
    return () => {
      clearInterval(refreshInterval);
      socketService.disconnect();
    };
  }, []);

  const getFilteredSubmissions = useMemo(() => {
    let filtered;
    switch (activeFilter) {
      case 'pending':
        filtered = submissions.filter(sub => sub.status === 'Pending');
        break;
      case 'completed':
        filtered = submissions.filter(sub => sub.status === 'Completed');
        break;
      case 'photo-pending':
        filtered = submissions.filter(sub => sub.photoTaken !== 'Yes');
        break;
      case 'expired':
        filtered = submissions.filter(sub => sub.status === 'Expired');
        break;
      case 'cancelled':
        filtered = submissions.filter(sub => sub.status === 'Cancelled');
        break;
      default:
        filtered = submissions;
    }

    // Apply studio filter
    if (selectedStudio !== 'All Studios') {
      filtered = filtered.filter(sub => sub.siteCode === selectedStudio);
    }

    if (searchTerm) {
      filtered = filtered.filter(sub => 
        (sub.email && sub.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (sub.tokenNumber && sub.tokenNumber.toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (dateFilter.startDate || dateFilter.endDate) {
      filtered = filtered.filter(sub => {
        const submissionDate = new Date(sub.submittedAt);
        const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
        const endDate = dateFilter.endDate ? new Date(dateFilter.endDate + 'T23:59:59') : null;
        
        if (startDate && submissionDate < startDate) return false;
        if (endDate && submissionDate > endDate) return false;
        return true;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'submittedAt') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (sortConfig.key === 'queuePosition' || sortConfig.key === 'tokenNumber') {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        } else {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [submissions, activeFilter, selectedStudio, searchTerm, dateFilter, sortConfig]);

  const allFilteredSubmissions = getFilteredSubmissions;
  const totalPages = Math.ceil(allFilteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const filteredSubmissions = allFilteredSubmissions.slice(startIndex, startIndex + itemsPerPage);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div>Loading photo studio data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div>
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={fetchSubmissions}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', background: '#f8f9fa', fontFamily: 'Arial, sans-serif'}}>
      {/* Main Header */}
      <div style={{
        background: 'linear-gradient(135deg, #34495e 0%, #2c3e50 100%)',
        color: 'white',
        padding: '15px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          <span style={{fontSize: '24px'}}>📷</span>
          <h1 style={{margin: 0, fontSize: '22px', fontWeight: '300', letterSpacing: '0.5px'}}>
            Photo Studio Queue Management
          </h1>
        </div>
        
        <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
          <div style={{display: 'flex', gap: '5px'}}>
            <button 
              onClick={() => setActiveView('dashboard')}
              style={{
                background: activeView === 'dashboard' ? '#007bff' : '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              📊 Dashboard
            </button>
            <button 
              onClick={() => setActiveView('calendar')}
              style={{
                background: activeView === 'calendar' ? '#007bff' : '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              📅 Calendar
            </button>
            <button 
              onClick={() => setActiveView('queue')}
              style={{
                background: activeView === 'queue' ? '#007bff' : '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              🔴 Live Queue
            </button>
          </div>
          
          <button onClick={fetchSubmissions} style={{
            background: '#ff9900',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            🔄 Refresh
          </button>
          
          <div style={{position: 'relative', display: 'inline-block'}}>
            <select 
              value={selectedStudio}
              onChange={(e) => setSelectedStudio(e.target.value)}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                appearance: 'none',
                paddingRight: '30px',
                minWidth: '150px'
              }}
            >
              <option value="All Studios">All Studios</option>
              <option value="BLR13">BLR13 Studio</option>
              <option value="BLR14">BLR14 Studio</option>
              <option value="BLR18">BLR18 Studio</option>
              <option value="BLR26">BLR26 Studio</option>
              <option value="BLR27">BLR27 Studio</option>
            </select>
            <div style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: 'white'
            }}>▼</div>
          </div>
          
          <button
            onClick={() => setShowHolidayForm(!showHolidayForm)}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            🗓️ Manage Holidays
          </button>
          
          <button
            onClick={() => setShowWalkIn(!showWalkIn)}
            style={{
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            🚶 Walk-in
          </button>
          
          <Link to="/studio-tv-display" style={{
            background: '#6c757d',
            color: 'white',
            textDecoration: 'none',
            padding: '10px 15px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            📊 Studio Status
          </Link>
          
          <Link to="/" style={{
            background: '#6c757d',
            color: 'white',
            textDecoration: 'none',
            padding: '10px 15px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            ← Back to Booking
          </Link>
        </div>
      </div>

      {/* Calendar View */}
      {activeView === 'calendar' && (
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label>Select Studio: </label>
            <select 
              value={selectedStudio === 'All Studios' ? 'BLR13' : selectedStudio}
              onChange={(e) => setSelectedStudio(e.target.value)}
              style={{ padding: '8px', marginLeft: '10px' }}
            >
              <option value="BLR13">BLR13</option>
              <option value="BLR18">BLR18</option>
            </select>
          </div>
          <CalendarView selectedStudio={selectedStudio === 'All Studios' ? 'BLR13' : selectedStudio} />
        </div>
      )}
      
      {/* Queue View */}
      {activeView === 'queue' && (
        <div style={{ padding: '20px' }}>
          <RealTimeQueue studioCode="BLR13" />
          <RealTimeQueue studioCode="BLR18" />
        </div>
      )}
      
      {/* Dashboard View */}
      {activeView === 'dashboard' && (
        <div>
          {/* Search Section */}
          <div style={{
            background: 'white',
            padding: '20px',
            borderBottom: '1px solid #e0e0e0'
          }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          <input
            type="text"
            placeholder="Search by email or token number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              minWidth: '250px',
              padding: '10px 15px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          <input
            type="date"
            placeholder="mm / dd / yyyy"
            value={dateFilter.startDate}
            onChange={(e) => setDateFilter(prev => ({...prev, startDate: e.target.value}))}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              width: '140px'
            }}
          />
          <input
            type="date"
            placeholder="mm / dd / yyyy"
            value={dateFilter.endDate}
            onChange={(e) => setDateFilter(prev => ({...prev, endDate: e.target.value}))}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              width: '140px'
            }}
          />
          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter({startDate: '', endDate: ''});
            }}
            style={{
              padding: '10px 15px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            All Dates
          </button>
          <button
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              setSearchTerm('');
              setDateFilter({startDate: today, endDate: today});
            }}
            style={{
              padding: '10px 15px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Today Only
          </button>
          <button 
            onClick={exportToExcel}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            📊 Export to Excel
          </button>
        </div>
      </div>

      {/* Holiday Management Form */}
      {showHolidayForm && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          padding: '20px',
          margin: '0 20px'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h3 style={{margin: '0 0 15px 0', color: '#856404'}}>🗓️ Holiday Management</h3>
            
            <div style={{display: 'flex', gap: '15px', alignItems: 'end', marginBottom: '20px'}}>
              <div>
                <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500'}}>Date:</label>
                <input
                  type="date"
                  value={newHolidayDate}
                  onChange={(e) => setNewHolidayDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500'}}>Reason:</label>
                <input
                  type="text"
                  value={newHolidayReason}
                  onChange={(e) => setNewHolidayReason(e.target.value)}
                  placeholder="e.g., Diwali, Christmas"
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '200px'
                  }}
                />
              </div>
              <button
                onClick={handleAddHoliday}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Add Holiday
              </button>
            </div>
            
            <div>
              <h4 style={{margin: '0 0 10px 0', color: '#856404'}}>Current Holidays:</h4>
              {holidays.length === 0 ? (
                <p style={{color: '#6c757d', fontStyle: 'italic'}}>No holidays configured</p>
              ) : (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                  {holidays.map((holiday, index) => (
                    <div key={index} style={{
                      background: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px'
                    }}>
                      <span>{new Date(holiday.date).toLocaleDateString()} - {holiday.reason}</span>
                      <button
                        onClick={() => handleRemoveHoliday(holiday.date)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '0'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Walk-in Form */}
      {showWalkIn && (
        <div style={{
          background: '#d1ecf1',
          border: '1px solid #bee5eb',
          padding: '20px',
          margin: '0 20px'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{margin: '0 0 15px 0', color: '#0c5460'}}>🚶 Quick Walk-in Booking</h3>
            <p style={{color: '#0c5460', fontSize: '14px', margin: '0 0 15px 0'}}>
              Auto-assigns to the current active time slot with the next available position.
            </p>
            <div style={{display: 'flex', gap: '15px', alignItems: 'end'}}>
              <div>
                <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500'}}>Email:</label>
                <input
                  type="email"
                  value={walkInEmail}
                  onChange={(e) => setWalkInEmail(e.target.value)}
                  placeholder="alias@amazon.com"
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', width: '250px' }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500'}}>Studio:</label>
                <select
                  value={walkInStudio}
                  onChange={(e) => setWalkInStudio(e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                >
                  <option value="BLR13">BLR13</option>
                  <option value="BLR18">BLR18</option>
                </select>
              </div>
              <button
                onClick={handleWalkIn}
                disabled={walkInLoading}
                style={{
                  background: walkInLoading ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '4px',
                  cursor: walkInLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {walkInLoading ? 'Adding...' : 'Add Walk-in'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Section */}
      {activeView === 'dashboard' && submissions.length > 0 && (
        <div style={{ background: '#f0f4f8', padding: '20px', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#2c3e50', fontSize: '16px' }}>📈 Analytics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
              {(() => {
                const today = new Date().toDateString();
                const todayBookings = submissions.filter(s => 
                  new Date(s.submittedAt || s.createdAt).toDateString() === today
                );
                const todayCompleted = todayBookings.filter(s => s.status === 'Completed').length;
                const todayTotal = todayBookings.length;
                const completionRate = todayTotal > 0 ? Math.round((todayCompleted / todayTotal) * 100) : 0;

                // Average wait: difference between submittedAt and updatedAt for completed bookings
                const completedWithTimes = todayBookings.filter(s => 
                  s.status === 'Completed' && s.submittedAt && s.updatedAt
                );
                let avgWaitMin = 0;
                if (completedWithTimes.length > 0) {
                  const totalWait = completedWithTimes.reduce((sum, s) => {
                    const submitted = new Date(s.submittedAt || s.createdAt).getTime();
                    const completed = new Date(s.updatedAt).getTime();
                    return sum + (completed - submitted);
                  }, 0);
                  avgWaitMin = Math.round(totalWait / completedWithTimes.length / 60000);
                }

                // Busiest slot today
                const slotCounts = {};
                todayBookings.forEach(s => {
                  const slot = s.timeSlot || (s.estimatedTime?.includes('|') ? s.estimatedTime.split('|')[1] : 'Unknown');
                  slotCounts[slot] = (slotCounts[slot] || 0) + 1;
                });
                const busiestSlot = Object.entries(slotCounts).sort((a, b) => b[1] - a[1])[0];

                // Bookings per studio today
                const studioCounts = {};
                todayBookings.forEach(s => {
                  studioCounts[s.siteCode] = (studioCounts[s.siteCode] || 0) + 1;
                });

                // No-shows: Expired bookings today
                const noShows = todayBookings.filter(s => s.status === 'Expired').length;

                return (
                  <>
                    <div style={{ background: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: completionRate >= 80 ? '#28a745' : completionRate >= 50 ? '#ffc107' : '#dc3545' }}>
                        {completionRate}%
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Today's Completion</div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{todayCompleted}/{todayTotal}</div>
                    </div>

                    <div style={{ background: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#007bff' }}>
                        {avgWaitMin > 0 ? `${avgWaitMin}m` : 'N/A'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Avg Wait Time</div>
                    </div>

                    <div style={{ background: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff9900' }}>
                        {busiestSlot ? busiestSlot[0] : 'N/A'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Busiest Slot</div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{busiestSlot ? `${busiestSlot[1]} bookings` : ''}</div>
                    </div>

                    <div style={{ background: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#6f42c1' }}>
                        {Object.entries(studioCounts).map(([code, count]) => `${code}: ${count}`).join(', ') || 'N/A'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Bookings/Studio</div>
                    </div>

                    <div style={{ background: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: noShows > 0 ? '#dc3545' : '#28a745' }}>
                        {noShows}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>No-Shows Today</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        background: '#f8f9fa',
        padding: '30px 20px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px'
        }}>
          <div 
            onClick={() => setActiveFilter('all')}
            style={{
              background: 'white',
              padding: '30px 20px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #007bff',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              transform: activeFilter === 'all' ? 'translateY(-2px)' : 'none'
            }}
          >
            <div style={{fontSize: '48px', fontWeight: 'bold', color: '#007bff', marginBottom: '8px', lineHeight: '1'}}>
              {allFilteredSubmissions.length}
            </div>
            <div style={{color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
              TOTAL APPOINTMENTS
            </div>
          </div>
          
          <div 
            onClick={() => setActiveFilter('photo-pending')}
            style={{
              background: 'white',
              padding: '30px 20px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #ffc107',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              transform: activeFilter === 'photo-pending' ? 'translateY(-2px)' : 'none'
            }}
          >
            <div style={{fontSize: '48px', fontWeight: 'bold', color: '#ffc107', marginBottom: '8px', lineHeight: '1'}}>
              {allFilteredSubmissions.filter(s => s.status === 'Pending' || s.status === 'In Progress').length}
            </div>
            <div style={{color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
              PENDING PHOTOS
            </div>
          </div>
          
          <div 
            onClick={() => setActiveFilter('completed')}
            style={{
              background: 'white',
              padding: '30px 20px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #28a745',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              transform: activeFilter === 'completed' ? 'translateY(-2px)' : 'none'
            }}
          >
            <div style={{fontSize: '48px', fontWeight: 'bold', color: '#28a745', marginBottom: '8px', lineHeight: '1'}}>
              {allFilteredSubmissions.filter(s => s.status === 'Completed').length}
            </div>
            <div style={{color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
              PHOTOS COMPLETED
            </div>
          </div>
          
          <div style={{
            background: 'white',
            padding: '30px 20px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #6f42c1',
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}>
            <div style={{fontSize: '48px', fontWeight: 'bold', color: '#6f42c1', marginBottom: '8px', lineHeight: '1'}}>
              5
            </div>
            <div style={{color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500'}}>
              ACTIVE STUDIOS
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div style={{
        background: 'white',
        margin: '0',
        padding: '0 20px 40px'
      }}>
        <div style={{
          maxWidth: '100%',
          margin: '0 auto',
          background: 'white',
          borderRadius: '8px',
          overflowX: 'auto',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px'}}>
            <thead>
              <tr style={{background: '#f8f9fa'}}>
                <th 
                  onClick={() => handleSort('tokenNumber')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Token Number {getSortIcon('tokenNumber')}
                </th>
                <th 
                  onClick={() => handleSort('email')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Amazon Email ID {getSortIcon('email')}
                </th>
                <th 
                  onClick={() => handleSort('siteCode')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Studio Location {getSortIcon('siteCode')}
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Photo Status {getSortIcon('status')}
                </th>
                <th 
                  onClick={() => handleSort('estimatedTime')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Time Slot {getSortIcon('estimatedTime')}
                </th>
                <th 
                  onClick={() => handleSort('queuePosition')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Queue Position {getSortIcon('queuePosition')}
                </th>
                <th 
                  onClick={() => handleSort('estimatedTime')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Estimated Time {getSortIcon('estimatedTime')}
                </th>
                <th 
                  onClick={() => handleSort('submittedAt')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Submitted At {getSortIcon('submittedAt')}
                </th>
                <th 
                  onClick={() => handleSort('updatedBy')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Updated By {getSortIcon('updatedBy')}
                </th>
                <th 
                  onClick={() => handleSort('completionTime')}
                  style={{
                    padding: '15px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#495057', 
                    borderBottom: '1px solid #dee2e6', 
                    fontSize: '12px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Completion Time {getSortIcon('completionTime')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((submission) => (
                <tr key={submission.id} style={{borderBottom: '1px solid #f0f0f0'}}>
                  <td style={{padding: '12px', color: '#007bff', fontWeight: 'bold', fontSize: '13px'}}>
                    {submission.tokenNumber || 'N/A'}
                  </td>
                  <td style={{padding: '12px', fontSize: '13px'}}>
                    {submission.email}
                  </td>
                  <td style={{padding: '12px', fontWeight: '600', fontSize: '13px'}}>
                    {submission.siteCode} Studio
                  </td>
                  <td style={{padding: '12px'}}>
                    {submission.status === 'Cancelled' ? (
                      <span style={{
                        color: '#dc3545',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        ❌ Cancelled
                      </span>
                    ) : (
                      <div style={{position: 'relative', display: 'inline-block'}}>
                        <select 
                          value={submission.status || 'Pending'}
                          onChange={(e) => handleStatusChange(submission.id, e.target.value)}
                          style={{
                            background: submission.status === 'Completed' ? '#d4edda' : 
                                       submission.status === 'In Progress' ? '#cce5ff' :
                                       submission.status === 'Expired' ? '#f8d7da' : '#fff3cd',
                            color: submission.status === 'Completed' ? '#155724' : 
                                   submission.status === 'In Progress' ? '#004085' :
                                   submission.status === 'Expired' ? '#721c24' : '#856404',
                            border: submission.status === 'Completed' ? '1px solid #c3e6cb' : 
                                    submission.status === 'In Progress' ? '1px solid #99d6ff' :
                                    submission.status === 'Expired' ? '1px solid #f5c6cb' : '1px solid #ffeaa7',
                            padding: '4px 25px 4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500',
                            appearance: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="Pending">📋 Pending</option>
                          <option value="In Progress">⏳ In Progress</option>
                          <option value="Completed">✅ Completed</option>
                          <option value="Expired">⏰ Expired</option>
                          <option value="Cancelled">❌ Cancelled</option>
                        </select>
                        <div style={{
                          position: 'absolute',
                          right: '5px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          fontSize: '8px',
                          color: submission.status === 'Completed' ? '#155724' : 
                                 submission.status === 'In Progress' ? '#004085' :
                                 submission.status === 'Expired' ? '#721c24' : '#856404'
                        }}>▼</div>
                      </div>
                    )}
                  </td>
                  <td style={{padding: '12px', color: '#28a745', fontWeight: 'bold', fontSize: '13px'}}>
                    {submission.estimatedTime ? 
                      (submission.estimatedTime.includes('|') ? 
                        submission.estimatedTime.split('|')[1] : 
                        submission.estimatedTime
                      ) : 'N/A'
                    }
                  </td>
                  <td style={{padding: '12px', color: '#6f42c1', fontWeight: 'bold', fontSize: '13px'}}>
                    #{submission.queuePosition || 'N/A'}
                  </td>
                  <td style={{padding: '12px', fontSize: '13px'}}>
                    {submission.estimatedTime ? 
                      (submission.estimatedTime.includes('|') ? 
                        submission.estimatedTime.split('|')[0] : 
                        submission.estimatedTime
                      ) : 'N/A'
                    }
                  </td>
                  <td style={{padding: '12px', fontSize: '13px'}}>
                    {submission.submittedAt ? new Date(submission.submittedAt).toLocaleString() : 'N/A'}
                  </td>
                  <td style={{padding: '12px', color: '#6c757d', fontSize: '13px'}}>
                    {submission.updatedBy || 'N/A'}
                  </td>
                  <td style={{padding: '12px', fontSize: '13px'}}>
                    {submission.status === 'Completed' ? 
                      (submission.updatedAt ? new Date(submission.updatedAt).toLocaleString() : 'Completed') : 
                      '-'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            gap: '10px',
            borderTop: '1px solid #f0f0f0'
          }}>
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                background: currentPage === 1 ? '#6c757d' : '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: currentPage === 1 ? 0.6 : 1
              }}
            >
              Previous
            </button>
            <span style={{
              background: '#007bff',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                background: currentPage === totalPages ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: currentPage === totalPages ? 0.6 : 1
              }}
            >
              Next
            </button>
          </div>
          
          {/* Footer Info */}
          <div style={{
            textAlign: 'center',
            padding: '15px 20px',
            background: '#f8f9fa',
            borderTop: '1px solid #e0e0e0',
            fontSize: '13px',
            color: '#6c757d'
          }}>
            <div style={{marginBottom: '5px'}}>
              Last updated: {new Date().toLocaleString()}
            </div>
            <div style={{marginBottom: '5px'}}>
              Auto-refresh: Every 30 seconds
            </div>
            <div>
              Showing {filteredSubmissions.length} of {allFilteredSubmissions.length} appointments (Page {currentPage} of {totalPages}) {selectedStudio !== 'All Studios' ? `for ${selectedStudio} Studio` : 'across all studios'}
            </div>
          </div>
        </div>
      </div>
      
      {/* Development tools removed for production */}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;